import * as d3 from 'importers/d3-importer'

/**
 * Creates an SVG donut graph using D3.
 * @class DonutGraph
 */
export class DonutGraph {
  duration = 800;
  width = 260;
  height = 260;
  colors = [ '#7991d9', '#d95a2b', '#f2e852', '#f29849', '#29d9d9' ];
  //       priciple  ,   tax    ,  insuran ,    pmi   ,    hoa

  /**
   * Constructor - Called when creating a new instance of DonutGraph
   *
   * @constructs
   * @param {Element} element container
   */
  constructor(element) {
    this.svg = d3.select(element)
        .append('svg')
        .attr('width', this.width)
        .attr('height', this.height)
        .classed('donut-graph__graph', true)
        .append('g')
        .classed('donut-graph__group', true)

    this.svg.append('g').classed('donut-graph__slices', true)

    const totalValueLabel = this.svg.append('g')
        .classed('donut-graph__label-total', true)
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('x', 0)
        .attr('dy', 0)
        .attr('transform', 'translate(0 -20)')

    totalValueLabel.append('tspan')
        .classed('donut-graph__label-total-text', true)
        .attr('x', 0)
        .attr('dy', 0)

    totalValueLabel.append('tspan')
        .classed('donut-graph__label-total-value', true)
        .attr('x', 0)
        .attr('dy', '1.2em')

    this.svg.attr('transform', `translate(${[ this.width / 2, this.height / 2 ]})`)

    this.radius = Math.min(this.width, this.height) / 2
    this.pie = d3.pie().sort(null).value(d => d.value)

    // Pie graph size
    this.arc = d3.arc().outerRadius(this.radius * 0.9).innerRadius(this.radius * 0.75)
    this.outerArc = d3.arc().innerRadius(this.radius * 0.9).outerRadius(this.radius * 0.7)

    // Sets color
    this.color = d3.scaleOrdinal().range(this.colors).range(this.colors)
  }


  /**
   * Changes pie graph based ob given object data
   *
   * @param {Array} dataObject
   */
  change(dataObject) {
    let data = []
    let counter = 0

    for (const key in dataObject) {
      if (dataObject[key] !== undefined && dataObject[key] > 0) {
        data[counter++] = {
          label: key,
          value: dataObject[key]
        }
      }
    }

    // Sorts data by label
    data = data.sort((a, b) => d3.descending(a.value, b.value))

    let initialData = this.svg.select('.donut-graph__slices').selectAll('path.donut-graph__slice').data().map(d => d.data)

    if (initialData.length === 0) initialData = data

    const changedData = this._mergeWithFirstEqualZero(data, initialData)
    const changingData = this._mergeWithFirstEqualZero(initialData, data)

    this._changeSlices(changedData, changingData, data)
    this._changeLabels(data)
    this._changeTotalsLabel(changedData, changingData)
  }


  // private

  /**
   * Returns the lookup key
   *
   * @param {Object} d
   * @private
   *
   * @return {String} label
   *
   * @example
   * return = 'Principal & Interest'
   */
  _key(d) {
    return d.data.label
  }


  /**
   * Rounds and formats number to USD monetary format
   * @param {Number} value
   * @private
   *
   * @return {String} formatted string
   *
   * @example
   * input = 1441.832943833
   * output = '$1,442'
   */
  _formatToUSD(value) {
    return d3.format('$,.0f')(value)
  }

  /**
   * Builds the SVG arc slices
   *
   * @param {Array} changedData
   * @param {Array} changingData
   * @param {Array} data
   * @private
   */
  _changeSlices(changedData, changingData, data) {
    const _this = this
    let slice = _this.svg.select('.donut-graph__slices').selectAll('path.donut-graph__slice').data(_this.pie(changedData), _this._key)

    slice.enter()
        .insert('path')
        .classed('donut-graph__slice', true)
        .style('fill', d => _this.color(d.data.label))
        .each(function(d) {
          this._current = d // eslint-disable-line no-invalid-this
        })

    slice = _this.svg.select('.donut-graph__slices').selectAll('path.donut-graph__slice').data(_this.pie(changingData), _this._key)

    slice.transition().duration(_this.duration).attrTween('d', function(d) {
      const interpolate = d3.interpolate(this._current, d) // eslint-disable-line no-invalid-this

      return t => {
        this._current = interpolate(t) // eslint-disable-line no-invalid-this
        return _this.arc(this._current) // eslint-disable-line no-invalid-this
      }
    })

    slice = _this.svg.select('.donut-graph__slices').selectAll('path.donut-graph__slice').data(_this.pie(data), _this._key)

    slice.exit().transition().delay(_this.duration).duration(0).remove()
  }


  /**
   * Builds the labels
   *
   * @param {Array} data
   * @private
   */
  _changeLabels(data) {
    const _this = this

    const updateSelection = d3.select('.donut-graph__labels').selectAll('.donut-graph__label-container')
        .data(data, d => d.label)


    // update the changed label values
    updateSelection
        .select(function(d) {
          const labelValue = d3.select(this).select('.donut-graph__label-value') // eslint-disable-line no-invalid-this

          labelValue.text(_this._formatToUSD(d.value))
        })


    // enter and build nested div structure
    const parentDiv = updateSelection.enter().append('div')
        .classed('donut-graph__label-container', true)

    const labelDiv = parentDiv.append('div')
        .classed('donut-graph__label', true)

    labelDiv.append('div')
        .classed('donut-graph__label-color', true)
        .style('background-color', d => _this.color(d.label))

    labelDiv.append('div')
        .classed('donut-graph__label-text', true)
        .text(d => d.label)

    parentDiv.append('div')
        .classed('donut-graph__label-value', true)
        .text(d => _this._formatToUSD(d.value))


    // exit/remove
    updateSelection.exit().remove()
  }


  /**
   * Applies the overall total monthly payment to center of pie chart
   *
   * @param {Array} changedData
   * @param {Array} changingData
   * @private
   */
  _changeTotalsLabel(changedData, changingData) {
    const _this = this
    const changedTotalValue = Object.values(changedData).reduce((a, b) => a + b.value, 0)
    const changingTotalValue = Object.values(changingData).reduce((a, b) => a + b.value, 0)
    const labelTotalText = _this.svg.select('tspan.donut-graph__label-total-text')
    const labelTotalValue = _this.svg.select('tspan.donut-graph__label-total-value')

    labelTotalText.text('Monthly Payment')

    labelTotalValue
        .text(changedTotalValue)
        .transition()
        .duration(this.duration)
        .tween('text', function() {
          const interpolate = d3.interpolateNumber(this.textContent.replace(/[$,]/g, ''), changingTotalValue) // eslint-disable-line no-invalid-this

          return t => {
            this.textContent = _this._formatToUSD(interpolate(t)) // eslint-disable-line no-invalid-this
          }
        })
  }


  /**
   * Removes or adds in new pie arcs to the graph
   *
   * @param {Array.<Object>} first - the labels and values for the donut
   * @param {Array.<Object>} second - the potentially new sets for the donut
   * @private
   *
   * @return {Array.<Object>} The new combined set
   */
  _mergeWithFirstEqualZero(first, second) {
    const secondSet = d3.set()

    second.forEach(d => secondSet.add(d.label))

    const onlyFirst = first.filter(d => !secondSet.has(d.label))
        .map(d => ({ label: d.label, value: 0 }))

    return d3.merge([ second, onlyFirst ]).sort((a, b) => d3.descending(a.value, b.value))
  }
}
